// Módulo Fiscal → Área do Contador: fechamento do mês (resumo, pendências,
// CFOP/CST) e o pacote .zip com todos os XMLs + PDF, para baixar ou mandar
// por e-mail ao contador.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import s from '../../pages/Financeiro.module.css';

const fmtMoeda = v => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtDT = d => d ? new Date(d).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—';
const fmtTam = b => b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;
const rotuloComp = c => { const [a, m] = (c || '').split('-'); return m ? `${m}/${a}` : c; };

// Competência padrão: o mês anterior (é o que o contador fecha).
export function competenciaAnterior(hoje = new Date()) {
  const d = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const COR_PEND = { alta: '#b91c1c', atencao: '#b45309' };

export default function AbaContador() {
  const [competencia, setCompetencia] = useState(competenciaAnterior());
  const [resumo, setResumo]       = useState(null);
  const [historico, setHistorico] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [ocupado, setOcupado]     = useState('');
  const [erro, setErro]           = useState('');
  const [aviso, setAviso]         = useState('');
  const [modalEmail, setModalEmail] = useState(false);
  const [email, setEmail]         = useState('');

  const carregar = useCallback(async () => {
    if (!/^\d{4}-\d{2}$/.test(competencia)) return;
    setCarregando(true); setErro('');
    try {
      const [r, h] = await Promise.all([
        api.get('/fiscal/contador/resumo', { params: { competencia } }),
        api.get('/fiscal/contador/envios'),
      ]);
      setResumo(r.data); setHistorico(h.data);
      setEmail(e => e || r.data.email_contador || '');
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao carregar o resumo do mês.');
    } finally { setCarregando(false); }
  }, [competencia]);
  useEffect(() => { carregar(); }, [carregar]);

  const baixar = async () => {
    setErro(''); setAviso(''); setOcupado('baixar');
    try {
      const resp = await api.get('/fiscal/contador/pacote', { params: { competencia }, responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url; a.download = `contador_${competencia}.zip`; a.click();
      URL.revokeObjectURL(url);
      setAviso(`Pacote de ${rotuloComp(competencia)} baixado.`);
      api.get('/fiscal/contador/envios').then(h => setHistorico(h.data)).catch(() => {});
    } catch (err) {
      let msg = 'Erro ao montar o pacote.';
      if (err.response?.data instanceof Blob) {
        try { msg = JSON.parse(await err.response.data.text()).erro || msg; } catch (_) {}
      } else msg = err.response?.data?.erro || msg;
      setErro(msg);
    } finally { setOcupado(''); }
  };

  const enviar = async () => {
    if (!email.trim()) { setErro('Informe o e-mail do contador.'); return; }
    setErro(''); setAviso(''); setOcupado('enviar');
    try {
      const { data } = await api.post('/fiscal/contador/pacote/enviar', { competencia, email: email.trim() });
      setModalEmail(false);
      setAviso(`Pacote de ${rotuloComp(competencia)} enviado para ${data.enviado_para} (${data.arquivos} arquivo(s)).`);
      api.get('/fiscal/contador/envios').then(h => setHistorico(h.data)).catch(() => {});
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao enviar o e-mail.');
    } finally { setOcupado(''); }
  };

  const r = resumo;
  const canceladas = r ? r.vendas.reduce((t, v) => t + v.canceladas, 0) : 0;

  return (
    <div>
      <div className={s.toolbar} style={{ flexWrap: 'wrap', alignItems: 'center' }}>
        <label className={s.label} style={{ margin: 0 }}>Competência</label>
        <input type="month" className={s.input} style={{ width: 170 }} value={competencia}
          onChange={e => setCompetencia(e.target.value)} />
        <button className={s.btnErp} onClick={carregar} disabled={carregando}>🔄 Atualizar</button>
        <span style={{ flex: 1 }} />
        <button className={s.btnErp} onClick={baixar} disabled={!!ocupado || !r}>
          {ocupado === 'baixar' ? 'Montando pacote...' : '🗜️ Baixar pacote do mês (.zip)'}
        </button>
        <button className={s.btnErpPrimario} onClick={() => { setErro(''); setModalEmail(true); }} disabled={!!ocupado || !r}>
          ✉ Enviar ao contador
        </button>
      </div>

      <div style={{ padding: '0 20px 20px' }}>
      {erro  && <div className={s.erroMsg}>{erro}</div>}
      {aviso && <div className={s.sucessoMsg}>{aviso}</div>}
      {carregando && !r && <div style={{ padding: 16, color: '#666' }}>Carregando...</div>}

      {r && (
        <>
          <div className={s.totaisRow} style={{ marginTop: 12 }}>
            {r.vendas.map(v => (
              <div key={v.tipo} className={s.totalCard}>
                <div className={s.valor}>{v.autorizadas}</div>
                <div className={s.rotulo}>{v.rotulo} autorizadas · {fmtMoeda(v.valor_autorizado)}</div>
              </div>
            ))}
            <div className={`${s.totalCard} ${canceladas ? s.totalCardVermelho : ''}`}>
              <div className={s.valor}>{canceladas}</div>
              <div className={s.rotulo}>Canceladas</div>
            </div>
            <div className={s.totalCard}>
              <div className={s.valor}>{r.compras.notas}</div>
              <div className={s.rotulo}>Notas de compra · {fmtMoeda(r.compras.valor)}</div>
            </div>
            <div className={s.totalCard}>
              <div className={s.valor}>{r.inutilizacoes.length}</div>
              <div className={s.rotulo}>Inutilizações</div>
            </div>
          </div>

          <div className={s.secao}>
            <div className={s.secaoTitulo}>Conferência antes de enviar</div>
            {!r.pendencias.length ? (
              <div style={{ color: '#166534', fontWeight: 600, padding: '4px 0' }}>
                ✅ Nenhuma pendência em {rotuloComp(r.competencia)}: pode mandar para o contador.
              </div>
            ) : r.pendencias.map((p, i) => (
              <div key={i} style={{ padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ fontWeight: 600, color: COR_PEND[p.gravidade] || '#b45309' }}>
                  {p.gravidade === 'alta' ? '⛔' : '⚠️'} {p.texto}
                </div>
                {p.detalhe && <div style={{ fontSize: 12, color: '#555', marginTop: 2 }}>{p.detalhe}</div>}
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {[['Saídas por CFOP', r.por_cfop, 'CFOP'], ['Saídas por CST/CSOSN do ICMS', r.por_cst, 'Situação tributária']].map(([tit, lista, col]) => (
              <div key={tit} className={s.secao} style={{ flex: '1 1 340px', minWidth: 0 }}>
                <div className={s.secaoTitulo}>{tit} <span style={{ fontWeight: 400, color: '#666' }}>(NF-e e NFC-e autorizadas)</span></div>
                {!lista.length ? <div style={{ color: '#666', fontSize: 13 }}>Sem notas autorizadas no mês.</div> : (
                  <table className={s.tabela}>
                    <thead><tr><th>{col}</th><th style={{ textAlign: 'right' }}>Itens</th><th style={{ textAlign: 'right' }}>Valor</th></tr></thead>
                    <tbody>
                      {lista.map(c => (
                        <tr key={c.codigo}><td>{c.codigo}</td><td style={{ textAlign: 'right' }}>{c.itens}</td><td style={{ textAlign: 'right' }}>{fmtMoeda(c.valor)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))}
          </div>

          {!!r.inutilizacoes.length && (
            <div className={s.secao}>
              <div className={s.secaoTitulo}>Inutilizações do mês</div>
              <table className={s.tabela}>
                <thead><tr><th>Documento</th><th>Série</th><th>Números</th><th>Protocolo</th><th>Justificativa</th></tr></thead>
                <tbody>{r.inutilizacoes.map((i, k) => (
                  <tr key={k}><td>{i.rotulo}</td><td>{i.serie}</td><td>{i.faixa}</td><td>{i.protocolo || '—'}</td><td>{i.justificativa}</td></tr>
                ))}</tbody>
              </table>
            </div>
          )}

          <div className={s.secao}>
            <div className={s.secaoTitulo}>O que vai no pacote</div>
            <div style={{ fontSize: 13, color: '#444', lineHeight: 1.6 }}>
              Um .zip com uma pasta por documento (NFC-e, NF-e, NFS-e, MDF-e), separando <b>autorizadas</b> e <b>canceladas</b> (com o XML do cancelamento),
              as <b>inutilizações</b>, as <b>notas de compra</b> e um <b>PDF de resumo</b> do mês. Os XMLs de NF-e e NFC-e vão com o
              protocolo de autorização da SEFAZ, o formato que os sistemas contábeis importam.
            </div>
          </div>
        </>
      )}

      <div className={s.secao}>
        <div className={s.secaoTitulo}>Histórico</div>
        {!historico.length ? <div style={{ color: '#666', fontSize: 13 }}>Nenhum pacote baixado ou enviado ainda.</div> : (
          <table className={s.tabela}>
            <thead><tr><th>Quando</th><th>Competência</th><th>Ação</th><th>Para</th><th style={{ textAlign: 'right' }}>Arquivos</th><th style={{ textAlign: 'right' }}>Tamanho</th><th>Usuário</th></tr></thead>
            <tbody>{historico.map(h => (
              <tr key={h.id}>
                <td>{fmtDT(h.criado_em)}</td><td>{rotuloComp(h.competencia)}</td>
                <td>{h.acao === 'email' ? '✉ E-mail' : '⬇ Download'}</td><td>{h.email || '—'}</td>
                <td style={{ textAlign: 'right' }}>{h.arquivos}</td><td style={{ textAlign: 'right' }}>{fmtTam(h.tamanho_bytes)}</td>
                <td>{h.usuario || '—'}</td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>

      </div>

      {modalEmail && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onMouseDown={e => e.target === e.currentTarget && !ocupado && setModalEmail(false)}>
          <div style={{ background: 'var(--surface, #fff)', borderRadius: 8, padding: 20, width: 440 }}>
            <div style={{ fontWeight: 'bold', marginBottom: 6 }}>Enviar o pacote de {rotuloComp(competencia)} ao contador</div>
            {!!r?.pendencias.length && (
              <div style={{ fontSize: 12, color: '#b45309', marginBottom: 8 }}>
                ⚠️ Há {r.pendencias.length} pendência(s) na conferência. Dá para enviar assim mesmo, mas o ideal é resolver antes.
              </div>
            )}
            <label className={s.label}>E-mail do contador</label>
            <input className={`${s.input} ${s.inputLarge}`} value={email} autoFocus
              onChange={e => setEmail(e.target.value)} placeholder="contador@escritorio.com.br"
              onKeyDown={e => e.key === 'Enter' && enviar()} />
            {erro && <div className={s.erroMsg} style={{ marginTop: 8 }}>{erro}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button className={s.btnErp} onClick={() => setModalEmail(false)} disabled={!!ocupado}>Cancelar</button>
              <button className={s.btnErpPrimario} onClick={enviar} disabled={!!ocupado}>
                {ocupado === 'enviar' ? 'Enviando...' : '✉ Enviar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
